import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import * as commentsApi from '../api/comments.api'

export const useCommentsStore = create(
  persist(
    (set, get) => ({
      comments: [],
      notifications: [],
      unreadCount: 0,
      readCommentExpiry: {},
      isLoading: false,
      isSubmitting: false,
      isNotificationLoading: false,

      fetchComments: async (leadId) => {
        set({ isLoading: true })

        try {
          const comments = await commentsApi.getComments(leadId)

          set({
            comments,
            isLoading: false,
          })

          return comments
        } catch (error) {
          set({
            comments: [],
            isLoading: false,
          })

          throw error
        }
      },

      fetchNotifications: async () => {
        set({ isNotificationLoading: true })

        try {
          const notifications = await commentsApi.getCommentNotifications()

          const now = Date.now()
          const { readCommentExpiry } = get()

          // Keep:
          // 1. All unread notifications
          // 2. Read notifications whose 1-minute expiry has not passed
          const filteredNotifications = notifications.filter((notification) => {
            if (!notification.isRead) {
              return true
            }

            const expiresAt = readCommentExpiry[notification.id]

            // If there is no local expiry, keep the notification.
            if (!expiresAt) {
              return true
            }

            return expiresAt > now
          })

          const unreadCount = filteredNotifications.filter(
            (notification) => !notification.isRead,
          ).length

          set({
            notifications: filteredNotifications,
            unreadCount,
            isNotificationLoading: false,
          })

          return filteredNotifications
        } catch (error) {
          set({
            notifications: [],
            unreadCount: 0,
            isNotificationLoading: false,
          })

          throw error
        }
      },

      fetchUnreadCount: async () => {
        const data = await commentsApi.getCommentNotificationUnreadCount()

        set({
          unreadCount: data.count,
        })

        return data
      },

      addNotification: (notification) =>
        set((state) => {
          const exists = state.notifications.some(
            (item) => item.id === notification.id,
          )

          if (exists) {
            return state
          }

          return {
            notifications: [notification, ...state.notifications],
            unreadCount: notification.isRead
              ? state.unreadCount
              : state.unreadCount + 1,
          }
        }),

      markNotificationsAsRead: async (notificationIds) => {
        if (!notificationIds?.length) {
          return
        }

        await commentsApi.markCommentNotificationsAsRead(notificationIds)

        set((state) => {
          const unreadIds = new Set(
            notificationIds.filter((id) =>
              state.notifications.some(
                (notification) =>
                  notification.id === id && !notification.isRead,
              ),
            ),
          )

          return {
            notifications: state.notifications.map((notification) =>
              notificationIds.includes(notification.id)
                ? {
                    ...notification,
                    isRead: true,
                    readAt: new Date().toISOString(),
                  }
                : notification,
            ),

            unreadCount: Math.max(0, state.unreadCount - unreadIds.size),
          }
        })
      },

      setReadCommentExpiry: (notificationIds) => {
        // TESTING: 1 minute
        const expiresAt = Date.now() + 1 * 60 * 1000

        set((state) => {
          const expiry = {
            ...state.readCommentExpiry,
          }

          notificationIds.forEach((notificationId) => {
            expiry[notificationId] = expiresAt
          })

          return {
            readCommentExpiry: expiry,
          }
        })
      },

      removeExpiredReadNotifications: () => {
        const now = Date.now()

        set((state) => {
          const expiredIds = Object.entries(state.readCommentExpiry)
            .filter(([, expiresAt]) => expiresAt <= now)
            .map(([notificationId]) => notificationId)

          if (!expiredIds.length) {
            return state
          }

          const expiredSet = new Set(expiredIds)

          const notifications = state.notifications.filter(
            (notification) => !expiredSet.has(notification.id),
          )

          const readCommentExpiry = {
            ...state.readCommentExpiry,
          }

          expiredIds.forEach((id) => {
            delete readCommentExpiry[id]
          })

          return {
            notifications,
            readCommentExpiry,
          }
        })
      },

      createComment: async (payload) => {
        if (get().isSubmitting) return null

        set({ isSubmitting: true })

        try {
          const comment = await commentsApi.createComment(payload)

          set((state) => {
            const exists = state.comments.some((c) => c.id === comment.id)

            return {
              comments: exists ? state.comments : [...state.comments, comment],
              isSubmitting: false,
            }
          })

          return comment
        } catch (error) {
          set({ isSubmitting: false })
          throw error
        }
      },

      addComment: (comment) =>
        set((state) => {
          const exists = state.comments.some((c) => c.id === comment.id)

          if (exists) {
            return state
          }

          return {
            comments: [...state.comments, comment],
          }
        }),

      replaceComment: (updatedComment) =>
        set((state) => ({
          comments: state.comments.map((comment) =>
            comment.id === updatedComment.id ? updatedComment : comment,
          ),
        })),

      removeComment: (commentId) =>
        set((state) => ({
          comments: state.comments.filter(
            (comment) => comment.id !== commentId,
          ),
        })),

      clearComments: () =>
        set({
          comments: [],
        }),
    }),
    {
      name: 'comments-storage',

      partialize: (state) => ({
        readCommentExpiry: state.readCommentExpiry,
      }),
    },
  ),
)
